/**
 * LandingPage — public landing page for Adversyn Trading.
 *
 * Positions Adversyn as an AI-powered wealth management firm built for
 * everyday investors, smaller account holders, and underserved communities.
 * Adversyn connects to supported brokerages over secure APIs; it does NOT
 * hold customer deposits. Includes a built-in investor education platform
 * and an AI tutor that explains markets, portfolios, and risk in plain
 * English.
 *
 * Compliance posture:
 *   - "wealth management firm" used as positioning, NOT a regulatory claim
 *   - No mention of being "registered" or "licensed"
 *   - No guaranteed-return language
 *   - Explicitly states Adversyn does not hold deposits
 *
 * Routing contract (from App.tsx):
 *   /            → this component
 *   /login       → AuthPage (sign-in tab)
 *   /register    → AuthPage (register tab)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Activity,
  Lock,
  CheckCircle2,
  Users,
  Brain,
  Link2,
  Newspaper,
  TrendingUp,
  Gauge,
  GraduationCap,
  BookOpen,
  MessageCircle,
  Heart,
  Compass,
  PiggyBank,
  Wallet,
  LineChart,
  Globe2,
  HelpCircle,
  Lightbulb,
  KeyRound,
  Eye,
} from 'lucide-react';

const ACCENT = '#22C55E';
const ACCENT_DARK = '#16a34a';
const ACCENT_SOFT = 'rgba(34,197,94,0.10)';
const BG = '#0a0a0f';
const PANEL = '#111118';
const PANEL_ALT = '#0e0e16';
const PANEL_BORDER = '#1e1e2e';
const PANEL_BORDER_SOFT = '#16162a';
const TEXT = '#e8e8ee';
const TEXT_SOFT = '#c8c8d4';
const MUTED = '#8a8aa0';

const PAGE_MAX = 1320;

const sectionStyle: React.CSSProperties = {
  padding: 'clamp(3rem, 5.5vw, 5rem) clamp(1.1rem, 4vw, 2.5rem)',
  maxWidth: PAGE_MAX,
  margin: '0 auto',
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 9,
  padding: '0.55rem 1rem',
  borderRadius: 999,
  border: `1px solid ${PANEL_BORDER}`,
  background: 'rgba(34,197,94,0.07)',
  color: ACCENT,
  fontSize: 12.5,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const trustChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 9,
  padding: '0.65rem 1.1rem',
  borderRadius: 999,
  border: `1px solid ${PANEL_BORDER}`,
  background: 'rgba(255,255,255,0.03)',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.04em',
  color: '#cdcde0',
};

const headlineStyle = (size: string): React.CSSProperties => ({
  fontFamily: 'Outfit, Inter, sans-serif',
  fontWeight: 900,
  fontStyle: 'italic',
  fontSize: size,
  lineHeight: 1.08,
  letterSpacing: '-0.02em',
  margin: 0,
  color: TEXT,
});

function SectionHeader(props: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  align?: 'center' | 'left';
}) {
  const align = props.align ?? 'center';
  return (
    <div
      style={{
        textAlign: align,
        maxWidth: align === 'center' ? 880 : '100%',
        margin: align === 'center' ? '0 auto' : 0,
      }}
    >
      <div style={{ ...chipStyle, marginBottom: 22 }}>{props.eyebrow}</div>
      <h2 style={headlineStyle('clamp(1.9rem, 4vw, 2.85rem)')}>{props.title}</h2>
      {props.subtitle && (
        <p
          style={{
            margin: align === 'center' ? '1.15rem auto 0' : '1.15rem 0 0',
            maxWidth: align === 'center' ? 760 : 760,
            fontSize: 'clamp(1.02rem, 1.45vw, 1.18rem)',
            lineHeight: 1.65,
            color: TEXT_SOFT,
          }}
        >
          {props.subtitle}
        </p>
      )}
    </div>
  );
}

function FeatureCard(props: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  badge?: string;
  minHeight?: number;
}) {
  return (
    <div
      data-card="feature"
      style={{
        background: PANEL,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: 18,
        padding: '1.8rem 1.7rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minHeight: props.minHeight ?? 220,
      }}
    >
      <div
        style={{
          width: 50,
          height: 50,
          background: ACCENT_SOFT,
          color: ACCENT,
          borderRadius: 14,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {props.icon}
      </div>
      {props.badge && (
        <span
          style={{
            alignSelf: 'flex-start',
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: ACCENT,
            background: ACCENT_SOFT,
            padding: '0.25rem 0.55rem',
            borderRadius: 999,
          }}
        >
          {props.badge}
        </span>
      )}
      <div
        style={{
          fontWeight: 800,
          fontSize: 17.5,
          lineHeight: 1.3,
          color: TEXT,
          letterSpacing: '-0.005em',
        }}
      >
        {props.title}
      </div>
      <div style={{ fontSize: 14.7, lineHeight: 1.65, color: TEXT_SOFT }}>{props.body}</div>
    </div>
  );
}

function StepCard(props: { step: number; title: string; body: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div
      style={{
        background: PANEL,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: 18,
        padding: '1.75rem 1.65rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        position: 'relative',
        minHeight: 240,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            background: ACCENT_SOFT,
            color: ACCENT,
            borderRadius: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {props.icon}
        </div>
        <div
          style={{
            fontFamily: 'Outfit, Inter, sans-serif',
            fontWeight: 900,
            fontStyle: 'italic',
            fontSize: 28,
            color: ACCENT,
            lineHeight: 1,
          }}
        >
          0{props.step}
        </div>
      </div>
      <div
        style={{
          fontWeight: 800,
          fontSize: 17.5,
          lineHeight: 1.3,
          color: TEXT,
        }}
      >
        {props.title}
      </div>
      <div style={{ fontSize: 14.7, lineHeight: 1.65, color: TEXT_SOFT }}>{props.body}</div>
    </div>
  );
}

function CompareRow(props: { traditional: string; adversyn: string; icon: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr 1fr',
        gap: 18,
        padding: '1.05rem 1.1rem',
        borderBottom: `1px solid ${PANEL_BORDER_SOFT}`,
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          background: ACCENT_SOFT,
          color: ACCENT,
          borderRadius: 10,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {props.icon}
      </div>
      <div
        style={{
          fontSize: 14.5,
          lineHeight: 1.55,
          color: MUTED,
          textDecoration: 'line-through',
          textDecorationColor: 'rgba(138,138,160,0.35)',
        }}
      >
        {props.traditional}
      </div>
      <div style={{ fontSize: 14.7, lineHeight: 1.55, color: TEXT, fontWeight: 600 }}>
        {props.adversyn}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const goToApp = () => navigate('/login');
  const goToBeta = () => navigate('/register');
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      data-testid="adversyn-landing"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(1400px 720px at 50% -10%, rgba(34,197,94,0.10), transparent 60%), ' +
          'radial-gradient(1100px 620px at 92% 14%, rgba(56,189,248,0.07), transparent 70%), ' +
          'radial-gradient(900px 520px at 8% 32%, rgba(168,85,247,0.05), transparent 70%), ' +
          BG,
        color: TEXT,
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      }}
    >
      {/* ===== TOP BAR ===== */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.15rem clamp(1rem, 4vw, 2.5rem)',
          borderBottom: `1px solid ${PANEL_BORDER}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(10,10,15,0.85)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            aria-hidden
            style={{
              width: 40,
              height: 40,
              background: ACCENT_DARK,
              borderRadius: 11,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 24px rgba(34,197,94,0.35)',
            }}
          >
            <span
              style={{
                color: '#fff',
                fontSize: 18,
                fontWeight: 900,
                fontStyle: 'italic',
                fontFamily: 'Outfit, Inter, sans-serif',
              }}
            >
              A
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <span
              style={{
                fontFamily: 'Outfit, Inter, sans-serif',
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: 14.5,
                letterSpacing: '-0.01em',
                textTransform: 'uppercase',
              }}
            >
              Adversyn
            </span>
            <span
              style={{
                fontFamily: 'Outfit, Inter, sans-serif',
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: 14.5,
                letterSpacing: '-0.01em',
                textTransform: 'uppercase',
                color: ACCENT,
              }}
            >
              Trading
            </span>
          </div>
        </div>

        <nav aria-label="Primary" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={goToApp}
            data-testid="landing-nav-enter"
            style={{
              background: 'transparent',
              border: `1px solid ${PANEL_BORDER}`,
              color: TEXT,
              padding: '0.6rem 1rem',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={goToBeta}
            data-testid="landing-nav-beta"
            style={{
              background: ACCENT,
              border: '1px solid transparent',
              color: '#062110',
              padding: '0.6rem 1rem',
              borderRadius: 10,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              boxShadow: '0 0 18px rgba(34,197,94,0.35)',
            }}
          >
            Request Early Access
          </button>
        </nav>
      </header>

      {/* ===== 1. HERO ===== */}
      <section
        data-section="hero"
        style={{
          padding: 'clamp(3.5rem, 8vw, 6.5rem) clamp(1.1rem, 4vw, 2.5rem) clamp(2.5rem, 5vw, 4rem)',
          maxWidth: PAGE_MAX,
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <div style={{ ...chipStyle, marginBottom: 26 }}>
          <Sparkles size={14} />
          Accessible AI-powered wealth management
        </div>

        <h1
          data-testid="landing-hero-headline"
          style={{
            fontFamily: 'Outfit, Inter, sans-serif',
            fontWeight: 900,
            fontStyle: 'italic',
            fontSize: 'clamp(2.55rem, 6.4vw, 4.6rem)',
            lineHeight: 1.02,
            letterSpacing: '-0.03em',
            margin: 0,
            color: TEXT,
          }}
        >
          A <span style={{ color: ACCENT }}>wealth management firm</span>
          <br />
          built for everyday investors.
        </h1>

        <p
          style={{
            margin: '1.85rem auto 0',
            maxWidth: 880,
            fontSize: 'clamp(1.08rem, 1.75vw, 1.28rem)',
            lineHeight: 1.6,
            color: TEXT_SOFT,
            fontWeight: 500,
          }}
        >
          Adversyn Trading is an AI-powered wealth management firm helping
          people <strong style={{ color: TEXT }}>grow, manage, protect, and understand</strong>{' '}
          their money &mdash; through secure broker-connected accounts,
          intelligent oversight, and education that meets you where you are.
          No high minimums. No assumption you&rsquo;re already an expert.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 14,
            justifyContent: 'center',
            marginTop: 38,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={goToBeta}
            data-testid="landing-cta-enter"
            style={{
              background: ACCENT,
              color: '#062110',
              border: '1px solid transparent',
              padding: '1.1rem 1.75rem',
              borderRadius: 14,
              fontWeight: 800,
              fontSize: 14.5,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              boxShadow: '0 0 30px rgba(34,197,94,0.42)',
            }}
          >
            Request Early Access <ArrowRight size={16} />
          </button>
          <button
            type="button"
            onClick={() => scrollTo('how-it-works')}
            data-testid="landing-cta-beta"
            style={{
              background: 'transparent',
              color: TEXT,
              border: `1px solid ${PANEL_BORDER}`,
              padding: '1.1rem 1.75rem',
              borderRadius: 14,
              fontWeight: 800,
              fontSize: 14.5,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            See How It Works
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            marginTop: 38,
            flexWrap: 'wrap',
          }}
        >
          <span style={trustChipStyle}>
            <ShieldCheck size={15} style={{ color: ACCENT }} /> Your money stays at your brokerage
          </span>
          <span style={trustChipStyle}>
            <Brain size={15} style={{ color: ACCENT }} /> AI-powered market intelligence
          </span>
          <span style={trustChipStyle}>
            <GraduationCap size={15} style={{ color: ACCENT }} /> Investor education + AI tutor
          </span>
        </div>
      </section>

      {/* ===== 2. TRUST / VALUE STRIP ===== */}
      <section
        data-section="trust-strip"
        style={{
          padding: 'clamp(1.25rem, 2.5vw, 2rem) clamp(1.1rem, 4vw, 2.5rem)',
          maxWidth: PAGE_MAX,
          margin: '0 auto',
        }}
      >
        <div
          style={{
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 18,
            background: PANEL_ALT,
            padding: 'clamp(1rem, 2vw, 1.4rem) clamp(1rem, 3vw, 1.75rem)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 18,
            alignItems: 'center',
          }}
        >
          {[
            { icon: <Wallet size={18} />, label: 'Wealth management for everyday investors' },
            { icon: <Link2 size={18} />, label: 'Broker-connected account management' },
            { icon: <Brain size={18} />, label: 'AI-powered market intelligence' },
            { icon: <BookOpen size={18} />, label: 'Investor education built in' },
            { icon: <MessageCircle size={18} />, label: 'AI tutor in plain language' },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 13.5,
                fontWeight: 700,
                color: TEXT_SOFT,
                lineHeight: 1.35,
              }}
            >
              <span style={{ color: ACCENT, flex: '0 0 auto', display: 'inline-flex' }}>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 3. WHO WE SERVE ===== */}
      <section data-section="who-we-serve" style={sectionStyle}>
        <SectionHeader
          eyebrow="Who we serve"
          title={
            <>
              Built for the people traditional wealth
              <br />
              management has <span style={{ color: ACCENT }}>left behind</span>.
            </>
          }
          subtitle="Adversyn is for everyone the legacy financial industry quietly excludes — through high minimums, opaque fees, and the assumption that you already know what you're doing."
        />

        <div
          data-grid="who-we-serve"
          className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6"
          style={{ marginTop: 56 }}
        >
          <FeatureCard
            icon={<Sparkles size={22} />}
            title="First-time investors"
            body="Start without feeling overwhelmed. Get real guidance and an AI tutor that explains what's happening, in language that actually makes sense."
            minHeight={230}
          />
          <FeatureCard
            icon={<PiggyBank size={22} />}
            title="Smaller account holders"
            body="No minimums that lock you out. No advisory fees that quietly eat the returns you worked for. Modern wealth management at a modern barrier."
            minHeight={230}
          />
          <FeatureCard
            icon={<Globe2 size={22} />}
            title="Underserved communities"
            body="Quality wealth management shouldn't depend on who your parents knew. Adversyn is built to expand access, not restrict it."
            minHeight={230}
          />
          <FeatureCard
            icon={<Compass size={22} />}
            title="Modern self-directed investors"
            body="If you want intelligent oversight without a high-touch advisor relationship, Adversyn gives you the intelligence — on your schedule."
            minHeight={230}
          />
        </div>
      </section>

      {/* ===== 4. HOW IT WORKS ===== */}
      <section id="how-it-works" data-section="how-it-works" style={sectionStyle}>
        <SectionHeader
          eyebrow="How Adversyn works"
          title={
            <>
              Modern wealth management
              <br />
              in <span style={{ color: ACCENT }}>four simple steps</span>.
            </>
          }
          subtitle="You keep custody of your money. Adversyn brings the intelligence, oversight, and education on top — through a secure connection to your brokerage."
        />

        <div
          className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6"
          style={{ marginTop: 56 }}
        >
          <StepCard
            step={1}
            icon={<Wallet size={22} />}
            title="Open or use a supported brokerage account"
            body="Your money lives at your brokerage — not with Adversyn. You stay in custody and in control of your funds at all times."
          />
          <StepCard
            step={2}
            icon={<KeyRound size={22} />}
            title="Connect it securely to Adversyn"
            body="Authenticate over a secure API. Credentials stay server-side, scoped per account. You define what Adversyn can read and act on."
          />
          <StepCard
            step={3}
            icon={<Brain size={22} />}
            title="Get AI-powered oversight and guidance"
            body="Market intelligence, account supervision, risk-aware decision support, and portfolio guidance — coordinated across your accounts."
          />
          <StepCard
            step={4}
            icon={<GraduationCap size={22} />}
            title="Learn as you go"
            body="Built-in investor education and an AI tutor make every decision understandable — so you grow confidence alongside your account."
          />
        </div>

        <div
          style={{
            marginTop: 38,
            padding: '1.2rem 1.4rem',
            border: `1px solid ${PANEL_BORDER}`,
            background: ACCENT_SOFT,
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Lock size={18} style={{ color: ACCENT }} />
          <span style={{ fontWeight: 700, fontSize: 14.5, color: TEXT }}>
            Adversyn is broker-connected. Your money lives at your brokerage &mdash; not with us.
          </span>
        </div>
      </section>

      {/* ===== 5. WHY ADVERSYN IS DIFFERENT ===== */}
      <section data-section="why-different" style={sectionStyle}>
        <SectionHeader
          eyebrow="Why Adversyn is different"
          title={
            <>
              Modern wealth management &mdash;
              <br />
              <span style={{ color: ACCENT }}>without the gatekeeping</span>.
            </>
          }
          subtitle="Traditional wealth management was built for households that already had wealth. We rebuilt it for the rest."
        />

        <div
          style={{
            marginTop: 56,
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 18,
            background: PANEL,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr 1fr',
              gap: 18,
              padding: '1rem 1.1rem',
              borderBottom: `1px solid ${PANEL_BORDER}`,
              background: PANEL_ALT,
            }}
          >
            <div />
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 800,
                color: MUTED,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Traditional wealth management
            </div>
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 800,
                color: ACCENT,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Adversyn
            </div>
          </div>

          <CompareRow
            icon={<Users size={18} />}
            traditional="High minimums to even start a relationship"
            adversyn="No minimums to learn and use the platform"
          />
          <CompareRow
            icon={<Wallet size={18} />}
            traditional="1%+ annual fees on assets under management"
            adversyn="Lower-barrier, technology-driven access"
          />
          <CompareRow
            icon={<Heart size={18} />}
            traditional="Designed for high-net-worth households"
            adversyn="Built for everyday investors and first-timers"
          />
          <CompareRow
            icon={<Activity size={18} />}
            traditional="Quarterly check-ins with a human advisor"
            adversyn="Continuous AI-powered intelligence and oversight"
          />
          <CompareRow
            icon={<GraduationCap size={18} />}
            traditional="Assumes you already understand investing"
            adversyn="Investor education and an AI tutor woven in"
          />
          <CompareRow
            icon={<ShieldCheck size={18} />}
            traditional="One-size-fits-most allocations"
            adversyn="Account-aware, risk-aware decision support"
          />
        </div>
      </section>

      {/* ===== 6. EDUCATION + AI TUTOR (major section) ===== */}
      <section data-section="education" style={sectionStyle}>
        <div
          style={{
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 24,
            padding: 'clamp(2rem, 4vw, 3rem)',
            background:
              'radial-gradient(900px 380px at 50% 0%, rgba(34,197,94,0.10), transparent 70%), ' +
              PANEL,
          }}
        >
          <SectionHeader
            eyebrow="Education + AI tutor"
            title={
              <>
                Learn as you invest. <span style={{ color: ACCENT }}>Ask anything, anytime</span>.
              </>
            }
            subtitle="Investing feels intimidating because nobody explains it in plain English. Adversyn changes that — built-in education and an AI tutor that's there in the exact moments confusion shows up."
          />

          <div
            className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6"
            style={{ marginTop: 48 }}
          >
            <FeatureCard
              icon={<BookOpen size={22} />}
              title="Investor education built in"
              body="Modules on portfolios, risk, market behavior, options, forex, and more — written for real people, at every level, with no jargon left unexplained."
              badge="Library"
              minHeight={250}
            />
            <FeatureCard
              icon={<MessageCircle size={22} />}
              title="AI tutor in plain English"
              body={
                <>
                  Ask &ldquo;what does this earnings report mean?&rdquo; or
                  &ldquo;why did my position move?&rdquo; &mdash; get a clear,
                  calm explanation, not a textbook.
                </>
              }
              badge="Always on"
              minHeight={250}
            />
            <FeatureCard
              icon={<Lightbulb size={22} />}
              title="Context-aware guidance"
              body="The tutor knows what's in your portfolio, what the market is doing, and what just changed — so its answers actually fit your situation, not a generic FAQ."
              badge="Personalized"
              minHeight={250}
            />
          </div>

          <div
            style={{
              marginTop: 32,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              justifyContent: 'center',
            }}
          >
            {[
              'What is a P/E ratio?',
              'Why is my account down today?',
              'Explain this options trade like I’m new.',
              'Is this a good time to rebalance?',
              'What does VIX mean for my portfolio?',
            ].map((q) => (
              <span
                key={q}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '0.55rem 0.95rem',
                  borderRadius: 999,
                  border: `1px solid ${PANEL_BORDER}`,
                  background: 'rgba(255,255,255,0.03)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: TEXT_SOFT,
                }}
              >
                <HelpCircle size={13} style={{ color: ACCENT }} />
                {q}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 7. CAPABILITIES ===== */}
      <section data-section="capabilities" style={sectionStyle}>
        <SectionHeader
          eyebrow="Capabilities"
          title={
            <>
              Everything you need to
              <br />
              <span style={{ color: ACCENT }}>manage money confidently</span>.
            </>
          }
          subtitle="The intelligence, oversight, and education of a modern wealth management firm — coordinated into one platform, available to everyday investors."
        />

        <div
          className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
          style={{ marginTop: 56 }}
        >
          <FeatureCard
            icon={<Brain size={22} />}
            title="AI-powered wealth intelligence"
            body="Cross-market analysis, news that actually matters, and AI reasoning you can follow step by step — applied to your portfolio, not a generic index."
          />
          <FeatureCard
            icon={<ShieldCheck size={22} />}
            title="Account supervision"
            body="Continuous oversight of your connected brokerage accounts. Guardrails for size, frequency, and exposure — configured by you, enforced by us."
          />
          <FeatureCard
            icon={<Gauge size={22} />}
            title="Risk management"
            body="Position sizing, exposure caps, drawdown awareness, and concentration checks — first-class citizens in every workflow."
          />
          <FeatureCard
            icon={<Link2 size={22} />}
            title="Broker-connected workflows"
            body="Securely linked to your supported brokerage. You retain custody. Adversyn provides the intelligence layer — not the deposit account."
          />
          <FeatureCard
            icon={<LineChart size={22} />}
            title="Market monitoring"
            body="Live equities, FX, volatility, and curated news distilled to what matters for your positions — not a firehose of headlines."
          />
          <FeatureCard
            icon={<GraduationCap size={22} />}
            title="Learning + AI tutor"
            body="Tutorials, glossaries, and an AI tutor woven into the platform. Confidence built alongside the account — never bolted on as an afterthought."
          />
        </div>
      </section>

      {/* ===== 8. MISSION / INCLUSION ===== */}
      <section data-section="mission" style={sectionStyle}>
        <div
          style={{
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 24,
            padding: 'clamp(2.25rem, 5vw, 3.5rem)',
            background:
              'radial-gradient(800px 320px at 50% 100%, rgba(34,197,94,0.10), transparent 70%), ' +
              PANEL_ALT,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 28,
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 880, margin: '0 auto' }}>
            <div style={{ ...chipStyle, marginBottom: 22 }}>
              <Heart size={14} /> Our mission
            </div>
            <h2 style={headlineStyle('clamp(1.95rem, 4.2vw, 3rem)')}>
              Wealth management that doesn&rsquo;t ask
              <br />
              you to <span style={{ color: ACCENT }}>already be wealthy</span>.
            </h2>
            <p
              style={{
                margin: '1.3rem auto 0',
                maxWidth: 820,
                fontSize: 'clamp(1.02rem, 1.45vw, 1.18rem)',
                lineHeight: 1.7,
                color: TEXT_SOFT,
              }}
            >
              Traditional wealth management was built for households that
              already had wealth. The advisors, the minimums, the fees, the
              relationships &mdash; quietly excluding the very people who
              would benefit most. Adversyn is building wealth management for
              everyone else: first-time investors, smaller account holders,
              underserved communities, and anyone told they don&rsquo;t
              qualify for &ldquo;real&rdquo; financial guidance. We use modern
              technology to lower the barriers &mdash; so quality wealth
              management is available to the people traditional finance has
              overlooked.
            </p>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6"
            style={{ marginTop: 8 }}
          >
            <FeatureCard
              icon={<Globe2 size={22} />}
              title="Accessibility first"
              body="Removing minimums, opaque fees, and gatekeeping that have priced everyday investors out of quality wealth management for decades."
              minHeight={210}
            />
            <FeatureCard
              icon={<Heart size={22} />}
              title="Inclusion by design"
              body="Built with underserved communities in mind from day one — not as an afterthought. Clear language, honest pricing, real guidance."
              minHeight={210}
            />
            <FeatureCard
              icon={<GraduationCap size={22} />}
              title="Education as a right"
              body="Quality financial education shouldn't be locked behind a private banker. Adversyn gives every user the explanations they deserve."
              minHeight={210}
            />
          </div>
        </div>
      </section>

      {/* ===== 9. FINAL CTA ===== */}
      <section
        data-section="cta"
        style={{ ...sectionStyle, paddingBottom: 'clamp(4rem, 7vw, 6rem)' }}
      >
        <div
          style={{
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 24,
            padding: 'clamp(2rem, 5vw, 3.5rem)',
            textAlign: 'center',
            background:
              'radial-gradient(900px 360px at 50% 0%, rgba(34,197,94,0.14), transparent 70%), ' +
              PANEL,
          }}
        >
          <div style={{ ...chipStyle, marginBottom: 22 }}>
            <Users size={14} />
            Early access &middot; Limited cohort
          </div>

          <h2 style={headlineStyle('clamp(1.95rem, 4.2vw, 2.9rem)')}>
            Accessible wealth management,
            <br />
            <span style={{ color: ACCENT }}>built for you</span>. Ready when you are.
          </h2>
          <p
            style={{
              margin: '1.2rem auto 0',
              maxWidth: 760,
              fontSize: 'clamp(1rem, 1.45vw, 1.13rem)',
              lineHeight: 1.65,
              color: TEXT_SOFT,
            }}
          >
            Request early access and experience AI-powered wealth management
            that helps you grow, manage, protect, and understand your
            money &mdash; with investor education and an AI tutor that meets
            you wherever you&rsquo;re starting.
          </p>

          <div
            style={{
              display: 'flex',
              gap: 14,
              justifyContent: 'center',
              marginTop: 30,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={goToBeta}
              data-testid="landing-cta-beta-bottom"
              style={{
                background: ACCENT,
                color: '#062110',
                border: '1px solid transparent',
                padding: '1.1rem 1.75rem',
                borderRadius: 14,
                fontWeight: 800,
                fontSize: 14.5,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                boxShadow: '0 0 30px rgba(34,197,94,0.42)',
              }}
            >
              Request Early Access <ArrowRight size={16} />
            </button>
            <button
              type="button"
              onClick={goToApp}
              style={{
                background: 'transparent',
                color: TEXT,
                border: `1px solid ${PANEL_BORDER}`,
                padding: '1.1rem 1.75rem',
                borderRadius: 14,
                fontWeight: 800,
                fontSize: 14.5,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              I already have an account
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 22,
              justifyContent: 'center',
              marginTop: 28,
              flexWrap: 'wrap',
              color: '#a3a3b8',
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
              <CheckCircle2 size={13} style={{ color: ACCENT }} /> Broker-connected
            </span>
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
              <CheckCircle2 size={13} style={{ color: ACCENT }} /> Education + AI tutor included
            </span>
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
              <CheckCircle2 size={13} style={{ color: ACCENT }} /> No deposits taken
            </span>
          </div>
        </div>
      </section>

      {/* ===== 10. FOOTER ===== */}
      <footer
        style={{
          borderTop: `1px solid ${PANEL_BORDER}`,
          padding: '2rem clamp(1.1rem, 4vw, 2.5rem) 2.5rem',
          background: PANEL_ALT,
        }}
      >
        <div
          style={{
            maxWidth: PAGE_MAX,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 22,
            paddingBottom: 22,
            borderBottom: `1px solid ${PANEL_BORDER_SOFT}`,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div
                aria-hidden
                style={{
                  width: 32,
                  height: 32,
                  background: ACCENT_DARK,
                  borderRadius: 9,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    fontFamily: 'Outfit, Inter, sans-serif',
                  }}
                >
                  A
                </span>
              </div>
              <span
                style={{
                  fontFamily: 'Outfit, Inter, sans-serif',
                  fontWeight: 900,
                  fontStyle: 'italic',
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  color: TEXT,
                }}
              >
                Adversyn Trading
              </span>
            </div>
            <p style={{ fontSize: 12.5, lineHeight: 1.6, color: MUTED, margin: 0, maxWidth: 280 }}>
              Accessible AI-powered wealth management &mdash; helping
              everyday investors grow, manage, protect, and understand their
              money.
            </p>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: TEXT,
                marginBottom: 10,
              }}
            >
              Platform
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                fontSize: 12.5,
                color: MUTED,
              }}
            >
              <li>Wealth intelligence</li>
              <li>Account supervision</li>
              <li>Risk management</li>
              <li>Broker-connected workflows</li>
            </ul>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: TEXT,
                marginBottom: 10,
              }}
            >
              Learn
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                fontSize: 12.5,
                color: MUTED,
              }}
            >
              <li>Investor education</li>
              <li>AI tutor</li>
              <li>Glossary</li>
              <li>Getting started</li>
            </ul>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: TEXT,
                marginBottom: 10,
              }}
            >
              Company
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                fontSize: 12.5,
                color: MUTED,
              }}
            >
              <li>Our mission</li>
              <li>Accessibility</li>
              <li>Contact</li>
              <li>Early access</li>
            </ul>
          </div>
        </div>

        <div
          style={{
            maxWidth: PAGE_MAX,
            margin: '22px auto 0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            color: MUTED,
            fontSize: 11.5,
            lineHeight: 1.6,
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <strong style={{ color: TEXT_SOFT }}>Important:</strong> Adversyn
            Trading is a technology platform. Investing involves risk,
            including the possible loss of principal. Adversyn does not hold
            customer funds &mdash; assets remain custodied at your supported
            brokerage. Adversyn does not provide guaranteed returns. Nothing
            on this site is an offer or solicitation to buy or sell any
            security.
          </div>
          <div
            style={{
              display: 'flex',
              gap: 18,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            <span>Risk disclosure</span>
            <span>Privacy</span>
            <span>Terms</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
